/**
 * Marketing source tracking.
 *
 * Captures a `from` URL parameter (e.g. `?from=ig-campaign-2`) so we can tell
 * which campaign a form submission originated from. The parameter can appear
 * either as a normal query string OR embedded inside the hash fragment, e.g.:
 *
 *   https://jobs.e-voque.com/?from=facebook-ads#join-us-form
 *   https://jobs.e-voque.com/#join-us-form?from=ig-campaign-2   <-- inside the hash
 *
 * The second form is what marketing actually uses, and its `from` lives in
 * `window.location.hash`, NOT in `window.location.search` (and is therefore
 * invisible to Next's `useSearchParams`). So we parse both.
 */

const SOURCE_PARAM = 'from';
const STORAGE_KEY = 'evoque_form_source';
/** Stored when a visitor arrives without any `from` param (direct traffic). */
export const DEFAULT_SOURCE = 'direct';

/**
 * Read the `from` parameter from the current URL, checking the query string
 * first and then the portion of the hash fragment after a `?`.
 * Returns null when not present. Safe to call during SSR (returns null).
 */
export function readFromParam(): string | null {
  if (typeof window === 'undefined') return null;

  // 1) Standard query string: ?from=...
  const fromSearch = new URLSearchParams(window.location.search).get(SOURCE_PARAM);
  if (fromSearch) return fromSearch;

  // 2) Embedded in the hash: #join-us-form?from=...
  const hash = window.location.hash;
  const queryInHash = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  if (queryInHash) {
    const fromHash = new URLSearchParams(queryInHash).get(SOURCE_PARAM);
    if (fromHash) return fromHash;
  }

  return null;
}

/** Read the persisted source for this session, if any. */
export function getStoredSource(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — ignore.
    return null;
  }
}

/**
 * Persist the source for this session. First-touch attribution: we only write
 * if nothing is stored yet, so the original campaign survives later navigation
 * that drops the param.
 */
export function setStoredSource(value: string): void {
  if (typeof window === 'undefined' || !value) return;
  try {
    if (!window.sessionStorage.getItem(STORAGE_KEY)) {
      window.sessionStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Private mode / storage quota — ignore.
  }
}

/**
 * Resolve the source to attach to a form submission.
 * Priority: live URL param → persisted session value → 'direct'.
 */
export function resolveSource(): string {
  return readFromParam() || getStoredSource() || DEFAULT_SOURCE;
}
